/**
 * ExecutionKind → DocumentExecutor — Document Layer I1 skeleton
 *
 * accounting_stream: backend stub — tenant frontend wires accountingDocumentApi (Voucher B);
 *   server-side executeAccountingStream still unwired (R7)
 * crud_record: Domain CRUD port (adapter injected at runtime)
 * stream_spawn: appeal spawn port (I3)
 * workflow_publish: knowledge policy/plan publish port (I4)
 *
 * I7: wrapWithLifecycleGate on execute/publish — lifecycle persisted via org_document_lifecycle (G4)
 * AdaptiveDecision Surface 연결 없음
 */

import { assertExecutable, DocumentNotApprovedError } from './org-document-lifecycle';
import type { DocumentApprovalFlags, ExecutionKind, OrgDocumentRecord } from './org-document-types';

export type DocumentExecutorAction =
  | 'save'
  | 'submit'
  | 'execute'
  | 'publish'
  | 'launch'
  | 'request_approval'
  | 'approve'
  | 'reject';

export interface DocumentExecutorInput {
  tenantId: string;
  documentType: string;
  action: DocumentExecutorAction;
  payload: Record<string, unknown>;
  mode: 'create' | 'edit' | 'view';
  /** I7 lifecycle gate — required for execute/publish when requiresApproval */
  record?: OrgDocumentRecord;
  flags?: DocumentApprovalFlags;
}

export interface DocumentExecutorResult {
  ok: boolean;
  error?: string;
  data?: unknown;
}

export type DocumentExecutor = (input: DocumentExecutorInput) => Promise<DocumentExecutorResult>;

/** Domain CRUD adapter — wired by tenant frontend (UniversalForm / backendApiClient). */
export interface DomainCrudExecutorPort {
  persist(input: DocumentExecutorInput): Promise<DocumentExecutorResult>;
}

/** Appeal stream_spawn adapter — wired in I3. */
export interface StreamSpawnExecutorPort {
  spawn(input: DocumentExecutorInput): Promise<DocumentExecutorResult>;
}

/** Knowledge workflow_publish adapter — wired in I4. */
export interface WorkflowPublishExecutorPort {
  publish(input: DocumentExecutorInput): Promise<DocumentExecutorResult>;
}

export class ExecutorNotWiredError extends Error {
  readonly executionKind: ExecutionKind;

  constructor(executionKind: ExecutionKind, detail?: string) {
    super(
      detail ?? `Executor for "${executionKind}" is not wired yet — see Document Layer stream plan`
    );
    this.name = 'ExecutorNotWiredError';
    this.executionKind = executionKind;
  }
}

let domainCrudPort: DomainCrudExecutorPort | null = null;
let streamSpawnPort: StreamSpawnExecutorPort | null = null;
let workflowPublishPort: WorkflowPublishExecutorPort | null = null;

export function registerDomainCrudExecutorPort(port: DomainCrudExecutorPort): void {
  domainCrudPort = port;
}

export function registerStreamSpawnExecutorPort(port: StreamSpawnExecutorPort): void {
  streamSpawnPort = port;
}

export function registerWorkflowPublishExecutorPort(port: WorkflowPublishExecutorPort): void {
  workflowPublishPort = port;
}

export function resetExecutorPortsForTests(): void {
  domainCrudPort = null;
  streamSpawnPort = null;
  workflowPublishPort = null;
}

const executeAccountingStream: DocumentExecutor = async () => {
  throw new ExecutorNotWiredError(
    'accounting_stream',
    'executeAccountingStream — wire in Voucher B (PostingDocumentShell / R7)'
  );
};

const persistViaDomainCrud: DocumentExecutor = async (input) => {
  if (!domainCrudPort) {
    throw new ExecutorNotWiredError(
      'crud_record',
      'Domain CRUD port not registered — inject via registerDomainCrudExecutorPort'
    );
  }
  return domainCrudPort.persist(input);
};

const spawnStreamFromDocument: DocumentExecutor = async (input) => {
  if (!streamSpawnPort) {
    throw new ExecutorNotWiredError(
      'stream_spawn',
      'Stream spawn port not registered — inject via registerStreamSpawnExecutorPort (I3)'
    );
  }
  return streamSpawnPort.spawn(input);
};

const publishWorkflow: DocumentExecutor = async (input) => {
  if (!workflowPublishPort) {
    throw new ExecutorNotWiredError(
      'workflow_publish',
      'Workflow publish port not registered — inject via registerWorkflowPublishExecutorPort (I4)'
    );
  }
  return workflowPublishPort.publish(input);
};

const bindSchedule: DocumentExecutor = async () => {
  throw new ExecutorNotWiredError('schedule_bind', 'bindSchedule — calendar Surface follow-up');
};

const LIFECYCLE_GATED_ACTIONS = new Set<DocumentExecutorAction>(['execute', 'publish']);

function wrapWithLifecycleGate(executor: DocumentExecutor): DocumentExecutor {
  return async (input) => {
    if (LIFECYCLE_GATED_ACTIONS.has(input.action)) {
      const record = input.record ?? {
        documentType: input.documentType,
        lifecycle: 'draft' as const,
      };
      const meta = {
        documentType: input.documentType,
        ...(input.flags !== undefined ? { flags: input.flags } : {}),
      };
      try {
        assertExecutable(record, meta);
      } catch (error) {
        if (error instanceof DocumentNotApprovedError) {
          return { ok: false, error: error.message };
        }
        throw error;
      }
    }
    return executor(input);
  };
}

const EXECUTOR_BY_KIND: Record<ExecutionKind, DocumentExecutor> = {
  accounting_stream: wrapWithLifecycleGate(executeAccountingStream),
  crud_record: persistViaDomainCrud,
  stream_spawn: spawnStreamFromDocument,
  workflow_publish: wrapWithLifecycleGate(publishWorkflow),
  schedule_bind: bindSchedule,
};

export function resolveExecutor(kind: ExecutionKind): DocumentExecutor {
  return EXECUTOR_BY_KIND[kind];
}

export { wrapWithLifecycleGate, assertExecutable, DocumentNotApprovedError };
